import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { translations } from "./translations";

// Idiomas disponibles y por defecto.
const LANGS = ["es", "en", "ja"];
const DEFAULT_LANG = "es";
const STORAGE_KEY = "suntaka_lang";

const LanguageContext = createContext(null);

// Idioma inicial: 1) el que la persona eligió antes (localStorage), 2) el del
// navegador si lo tenemos traducido, 3) español por defecto.
function detectInitialLang() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS.includes(saved)) return saved;
  } catch (e) {
    /* localStorage puede fallar en modo privado; seguimos con la detección */
  }
  const fromNavigator = (navigator.language || "").slice(0, 2).toLowerCase();
  return LANGS.includes(fromNavigator) ? fromNavigator : DEFAULT_LANG;
}

// Lee una clave con notación por puntos ("footer.hours") de un diccionario.
function readKey(dict, key) {
  return key
    .split(".")
    .reduce((obj, part) => (obj == null ? undefined : obj[part]), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      /* si no se puede guardar, no pasa nada: solo no se recuerda */
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (LANGS.includes(next)) setLangState(next);
  }, []);

  // Devuelve la traducción de la clave. Si falta en el idioma activo, cae al
  // español; si tampoco existe, devuelve la propia clave (para detectar fallos).
  const t = useCallback(
    (key) => {
      const active = readKey(translations[lang] || {}, key);
      if (active !== undefined) return active;
      const fallback = readKey(translations[DEFAULT_LANG] || {}, key);
      return fallback !== undefined ? fallback : key;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useI18n debe usarse dentro de <LanguageProvider>");
  }
  return ctx;
}
